import { withNamespaces } from "react-i18next";
import { Text, View } from "@react-pdf/renderer";
import styles from "../../Styles";

const OwnershipBreakdown = ({ t, safeJson, pct, company }) => {
  /** SECTION 5 DATA **/
  const ultimate = safeJson(company?.ultimateShareholderData);
  const graph = safeJson(company?.shareholderGraphData);

  const ultimateShareholders = (ultimate?.shareholders ?? [])
    .filter((s) => (s?.name ?? "").trim().length > 0)
    .map((s) => ({
      name: s.name,
      // country is NOT provided in your data -> keep "-"
      country: "-",
      ownershipPct: pct(Number(s.calculated_percentage)),
      depth: s?.max_depth ? `${s.max_depth} (Direct)` : "1 (Direct)",
      _pctNum: Number(s.calculated_percentage) || 0,
    }));

  // 5.1 table rows
  const ownershipBreakdownRows = ultimateShareholders.map((s) => ({
    shareholderName: s.name,
    country: s.country,
    ownershipPct: s.ownershipPct,
    depth: s.depth,
  }));

  // 5.1 summary text
  const totalIdentifiedPct = ultimateShareholders.reduce(
    (sum, s) => sum + (Number.isFinite(s._pctNum) ? s._pctNum : 0),
    0
  );
  const remainingPct = Math.max(0, 100 - totalIdentifiedPct);

  // Find "Streubesitz" (free float) in shareholderGraphData
  const streubesitzEntity =
    graph?.entities?.find(
      (e) => (e?.name ?? "").toLowerCase() === "streubesitz"
    ) ?? null;

  const streubesitzRel =
    graph?.relations?.find(
      (r) =>
        r?.type === "is_share_owner_of" &&
        r?.to === graph?.from_cin &&
        typeof r?.percentage === "number" &&
        // try to match Streubesitz via "from"
        r?.from === streubesitzEntity?.cin
    ) ?? null;

  const freeFloatPct =
    typeof streubesitzRel?.percentage === "number"
      ? streubesitzRel.percentage
      : typeof streubesitzEntity?.percentage === "number"
      ? streubesitzEntity.percentage
      : null;

  // 5.2 table rows (Transparency Register Information)
  const transparencyRegisterRows = [
    {
      shareholder: "Streubesitz (Free float)",
      type: "Public/Group",
      ownershipPct: freeFloatPct != null ? pct(Number(freeFloatPct)) : "-",
    },
    ...ultimateShareholders.map((s) => ({
      shareholder: s.name,
      type: "Company",
      ownershipPct: s.ownershipPct,
    })),
  ];

  // 5.3 subsidiaries/partnership structure derived from graph.relations
  // build CIN->Entity lookup
  const entityByCin = new Map((graph?.entities ?? []).map((e) => [e.cin, e]));

  const outgoingRels = (graph?.relations ?? []).filter(
    (r) => r?.from === graph?.from_cin
  );

  // classify relations to groups (based on your screenshot narrative style)
  const germanPartners = outgoingRels
    .filter((r) => r?.type === "is_german_partner_of")
    .map((r) => ({
      name: entityByCin.get(r.to)?.name ?? "-",
      percentage: typeof r.percentage === "number" ? r.percentage : null,
      label: "German Partner",
    }));

  const limitedPartners = outgoingRels
    .filter((r) => r?.type === "is_limited_partner_of")
    .map((r) => ({
      name: entityByCin.get(r.to)?.name ?? "-",
      percentage: typeof r.percentage === "number" ? r.percentage : null,
      label: "Limited Partner",
    }));

  // 5.3 bullet lists
  const subsidiaryPartnershipBullets = {
    coreOps: [
      ...germanPartners.map(
        (x) => `${x.name}: ${x.percentage ?? "-"}% (${x.label})`
      ),
    ],
    realEstate: [
      ...limitedPartners.map(
        (x) => `${x.name}: ${x.percentage ?? "-"}% (${x.label})`
      ),
    ],
  };

  return (
    <View style={styles.section} break>
      {/* Section title */}
      <Text style={styles.sectionSeparator}>
        {`${t("5.")} ${t("Ownership Breakdown by Entity")}`}
      </Text>

      {/* 5.1 */}
      <Text style={styles.alphListItem}>{`5.1 ${t("Shareholder")}`}</Text>

      <Text style={{ marginBottom: 8 }}>
        {`The total identified ownership remains at ${totalIdentifiedPct.toFixed(
          2
        )}%, with the remaining ${remainingPct.toFixed(
          2
        )}% likely representing public float or smaller investors.`}
      </Text>

      {/* 5.1 table */}
      <View style={styles.table}>
        {/* header */}
        <View style={styles.tableRow}>
          <View style={styles.tableColHeader}>
            <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
              {t("Shareholder Name")}
            </Text>
          </View>
          <View style={styles.tableColHeader}>
            <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
              {t("Country")}
            </Text>
          </View>
          <View style={styles.tableColHeader}>
            <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
              {t("Ownership %")}
            </Text>
          </View>
          <View style={{ ...styles.tableColHeader, ...styles.borderRightNone }}>
            <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
              {t("Depth")}
            </Text>
          </View>
        </View>

        {/* body */}
        {ownershipBreakdownRows.map((r, i) => {
          const rowStyle = {
            ...styles.tableRow,
            ...(i === ownershipBreakdownRows.length - 1
              ? styles.tableLastRow
              : {}),
          };

          return (
            <View key={i} style={rowStyle}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{r.shareholderName ?? "-"}</Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{r.country ?? "-"}</Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{r.ownershipPct ?? "-"}</Text>
              </View>
              <View style={{ ...styles.tableCol, ...styles.borderRightNone }}>
                <Text style={styles.tableCell}>{r.depth ?? "-"}</Text>
              </View>
            </View>
          );
        })}
      </View>

      {/* 5.2 */}
      <Text style={styles.alphListItem}>{`5.2 ${t(
        "Transparency Register Information"
      )}`}</Text>

      <Text style={{ marginBottom: 8 }}>
        {`The "Streubesitz" (Free float) explains the remaining gap from the previous data. The ownership is distributed as follows:`}
      </Text>

      {/* 5.2 table */}
      <View style={styles.table}>
        {/* header */}
        <View style={styles.tableRow}>
          <View style={styles.tableColHeader}>
            <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
              {t("Shareholder")}
            </Text>
          </View>
          <View style={styles.tableColHeader}>
            <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
              {t("Type")}
            </Text>
          </View>
          <View style={{ ...styles.tableColHeader, ...styles.borderRightNone }}>
            <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
              {t("Ownership %")}
            </Text>
          </View>
        </View>

        {/* body */}
        {transparencyRegisterRows.map((r, i) => {
          const rowStyle = {
            ...styles.tableRow,
            ...(i === transparencyRegisterRows.length - 1
              ? styles.tableLastRow
              : {}),
          };

          return (
            <View key={i} style={rowStyle}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{r.shareholder ?? "-"}</Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{r.type ?? "-"}</Text>
              </View>
              <View style={{ ...styles.tableCol, ...styles.borderRightNone }}>
                <Text style={styles.tableCell}>{r.ownershipPct ?? "-"}</Text>
              </View>
            </View>
          );
        })}
      </View>

      {/* 5.3 */}
      <Text style={styles.alphListItem}>{`5.3 ${t(
        "Subsidiary & Partnership Structure"
      )}`}</Text>

      <Text style={{ marginBottom: 8 }}>
        {`The company holds significant or total control over several functional and real estate entities, mostly identified as 100% stakes:`}
      </Text>

      <Text style={{ marginBottom: 4, ...styles.fontBold }}>
        {t("Core Operations & Holdings")}:
      </Text>
      <View style={{ marginLeft: 12, marginBottom: 8 }}>
        {(subsidiaryPartnershipBullets.coreOps ?? []).length ? (
          subsidiaryPartnershipBullets.coreOps.map((line, idx) => (
            <Text key={idx}>{`• ${line}`}</Text>
          ))
        ) : (
          <Text>{`• -`}</Text>
        )}
      </View>

      <Text style={{ marginBottom: 4, ...styles.fontBold }}>
        {t("Real Estate & Leasing (Project Entities)")}:
      </Text>
      <View style={{ marginLeft: 12 }}>
        {(subsidiaryPartnershipBullets.realEstate ?? []).length ? (
          subsidiaryPartnershipBullets.realEstate.map((line, idx) => (
            <Text key={idx}>{`• ${line}`}</Text>
          ))
        ) : (
          <Text>{`• -`}</Text>
        )}
      </View>
    </View>
  );
};

export default withNamespaces()(OwnershipBreakdown);
